import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import BlogPagination from '../blog/components/BlogPagination';
import Card from '../../components/base/Card';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  categories: number[];
  readTime: string;
  publishDate: string;
  slug: string;
  author: string;
  authorSlug: string;
  _embedded?: any;
}

interface Category {
  id: number;
  name: string;
  description: string;
  slug: string;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [avatars, setAvatars] = useState<Record<number, string>>({});
  const [categoriesMap, setCategoriesMap] = useState<Record<number, string>>({});
  const postsPerPage = 8;

  // Fetch all categories for pills
  useEffect(() => {
    const fetchAllCategories = async () => {
      try {
        const res = await fetch(`https://blog.thecryptotutor.com/wp-json/wp/v2/categories?per_page=100`);
        const data = await res.json();
        setAllCategories([{ id: 0, name: 'All', slug: 'all' }, ...data.map((c: any) => ({
          id: c.id,
          name: c.name,
          slug: c.slug,
          description: c.description
        }))]);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchAllCategories();
  }, []);

  // Fetch posts for current category
  useEffect(() => {
    const fetchCategoryAndPosts = async () => {
      setLoading(true);
      setError(null);

      try {
        let catId: number | undefined;

        if (slug && slug !== 'all') {
          // Fetch category by slug
          const categoryRes = await fetch(`https://blog.thecryptotutor.com/wp-json/wp/v2/categories?slug=${slug}`);
          const categoryData = await categoryRes.json();
          if (!categoryData || categoryData.length === 0) {
            setError('Category not found');
            setLoading(false);
            return;
          }
          const cat = categoryData[0];
          catId = cat.id;
          setCategory({
            id: cat.id,
            name: cat.name,
            description: cat.description,
            slug: cat.slug,
          });
        } else {
          setCategory(null); // "All" selected
        }

        // Fetch posts
        const postsRes = await fetch(
          `https://blog.thecryptotutor.com/wp-json/wp/v2/posts?per_page=100&_embed${
            catId ? `&categories=${catId}` : ''
          }`
        );
        const postsData = await postsRes.json();

        // Map posts
        const mappedPosts: BlogPost[] = postsData.map((p: any) => ({
          id: p.id,
          title: p.title.rendered,
          excerpt: p.excerpt.rendered,
          image: p._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/default-image.jpg',
          categories: p.categories,
          publishDate: new Date(p.modified).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
          slug: p.slug,
          author: p._embedded?.author?.[0]?.name || 'Unknown',
          authorSlug: p._embedded?.author?.[0]?.slug || '',
          _embedded: p._embedded,
          readTime: '', // optional
        }));

        setPosts(mappedPosts);

        // Build categories map
        const catMap: Record<number, string> = {};
        postsData.forEach((p: any) => {
          p._embedded?.['wp:term']?.forEach((group: any) => {
            group.forEach((term: any) => {
              if (term.taxonomy === 'category') catMap[term.id] = term.name;
            });
          });
        });
        setCategoriesMap(catMap);

        // Fetch author avatars
        const avatarMap: Record<number, string> = {};
        await Promise.all(
          postsData.map(async (p: any) => {
            const author = p._embedded?.author?.[0];
            if (!author) return;
            if (author.acf?.custom_avatar) {
              try {
                const mediaRes = await fetch(`https://blog.thecryptotutor.com/wp-json/wp/v2/media/${author.acf.custom_avatar}`);
                const mediaData = await mediaRes.json();
                avatarMap[author.id] = mediaData.source_url || '/default-avatar.jpg';
              } catch {
                avatarMap[author.id] = '/default-avatar.jpg';
              }
            } else if (author.avatar_urls?.['96']) {
              avatarMap[author.id] = author.avatar_urls['96'];
            } else {
              avatarMap[author.id] = '/default-avatar.jpg';
            }
          })
        );
        setAvatars(avatarMap);

      } catch (err: any) {
        console.error(err);
        setError('Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryAndPosts();
  }, [slug]);

  if (loading) return <div className="text-center py-20">Loading...</div>;
  if (error) return <div className="text-center py-20 text-red-500">{error}</div>;

  const totalPages = Math.ceil(posts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = posts.slice(startIndex, startIndex + postsPerPage);

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/categories" className="hover:text-blue-600 transition-colors">Categories</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900">{category?.name || 'All Posts'}</span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{category?.name || 'All Posts'}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">{category?.description}</p>

          {/* Category Pills */}
          <div className="flex justify-center gap-2 flex-wrap p-4">
            {allCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  if (cat.slug === 'all') navigate('/blog');
                  else navigate(`/category/${cat.slug}`);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  slug === cat.slug || (!slug && cat.slug === 'all')
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100 hover:text-blue-700'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {currentPosts.map((post) => {
              const authorData = post._embedded?.author?.[0];
              const authorAvatar = authorData ? avatars[authorData.id] : '/default-avatar.jpg';
              const postCategories = post.categories.map((id) => categoriesMap[id]).filter(Boolean).join(', ');

              return (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group h-full">
                  <Card hover className="flex flex-col h-full overflow-hidden">
                    <div className="aspect-video overflow-hidden flex-shrink-0">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      {/* Title */}
                      <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                        {post.title}
                      </h3>

                      {/* Author + Date */}
                      <div className="flex items-center justify-between text-xs text-gray-500 space-x-2">
                        <div className="flex items-center space-x-2">
                          <img src={authorAvatar} alt={authorData?.name || 'Author'} className="w-5 h-5 rounded-lg object-cover" />
                          {authorData && (
                            <Link
                              to={`/author/${authorData.slug}`}
                              className="hover:text-blue-600 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {authorData.acf?.full_name || authorData.name}
                            </Link>
                          )}
                        </div>
                        <span>{post.publishDate}</span>
                      </div>

                      {/* Categories */}
                      {postCategories && (
                        <div className="mt-2 text-xs text-blue-600 font-medium truncate">{postCategories}</div>
                      )}

                      {/* Optional readTime */}
                      {post.readTime && (
                        <div className="mt-1 text-xs text-gray-500">{post.readTime}</div>
                      )}
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>

          <BlogPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
}